package diana.soleil.design.iraniancanadienbazzar.core.data.fake

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow

/**
 * Debug-only switch for **mock-data mode** (Developer Menu → "Populate mock data" / "Clear mock
 * data"). When enabled, the app composition root rebuilds the whole Koin graph onto the in-memory
 * fake repositories seeded with [MockScenarios] — every screen shows demo data and nothing touches
 * Firebase (no Firestore reads/writes, no telemetry, no push registration). Disabling drops the
 * graph, which garbage-collects all mock state and returns to the live backend.
 *
 * A process-wide object (not DI) because the choice must be readable BEFORE the Koin graph is
 * built, and must survive the graph being rebuilt when it flips.
 *
 * Deliberately **process-lifetime only — never persisted**: a fresh launch ALWAYS starts on the
 * live backend. (The first version persisted the flag; a restarted app then silently ran on fake
 * data and broke QA deep links — and a restored preference could even have flipped a release build,
 * where no banner exists, onto mock data. Non-persistence kills that whole class: the only way in
 * is a tap in the debug-gated Developer Menu of the current process.)
 */
object MockDataController {

    private val _enabled = MutableStateFlow(false)

    /** Whether the app is currently running on seeded in-memory mock data. */
    val enabled: StateFlow<Boolean> = _enabled

    fun setEnabled(value: Boolean) {
        _enabled.value = value
    }

    private val _screenshotMode = MutableStateFlow(false)

    /**
     * Store-screenshot capture: mock data WITHOUT the 🧪 banner, so the marketing shots show the app
     * the way a member sees it. Set only from a platform launch switch that cannot exist on a shipped
     * device build (Android: a debug-build intent extra; iOS: a simulator-only launch argument), and
     * like [enabled] it is process-lifetime only.
     */
    val screenshotMode: StateFlow<Boolean> = _screenshotMode

    fun setScreenshotMode(value: Boolean) {
        _screenshotMode.value = value
    }
}
